import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Stack
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Print as PrintIcon,
  CheckCircle as CheckCircleIcon,
  LockOpen as LockOpenIcon,
  Cancel as CancelIcon,
  MoreVert as MoreVertIcon,
  History as HistoryIcon,
  Badge as BadgeIcon,
  ReceiptLong as ReceiptLongIcon
} from '@mui/icons-material';

const AlbaranHeaderBar = ({
  esNuevo,
  albaran,
  onVolver,
  onCerrar,
  onAnular,
  onReabrir,
  onFacturar,
  onImprimir,
  onVerReceptor,  // función extra que mencionaste
  onVerHistorial  // función extra que mencionaste
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const openMenu = Boolean(anchorEl);

  const handleOpenMenu = (event) => setAnchorEl(event.currentTarget);
  const handleCloseMenu = () => setAnchorEl(null);

  // Mapeo de estilos según estado
  const getEstadoChip = (estado) => {
    const config = {
      ABIERTO: { label: 'Abierto', color: 'info' },
      CERRADO: { label: 'Cerrado', color: 'warning' },
      FACTURADO: { label: 'Facturado', color: 'success' },
      ANULADO: { label: 'Anulado', color: 'error' }
    };
    const current = config[estado] || { label: estado || 'Borrador', color: 'default' };
    return <Chip label={current.label} color={current.color} size="small" sx={{ fontWeight: 600, borderRadius: 1.5 }} />;
  };

  const esEditable = esNuevo || albaran?.estado_albaran === 'ABIERTO';

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        mb: 3,
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 2
      }}
    >
      {/* Sección Izquierda: Navegación, Título y Estado */}
      <Stack direction="row" alignItems="center" spacing={2}>
        <Tooltip title="Volver a albaranes">
          <IconButton onClick={onVolver} size="small" sx={{ border: '1px solid', borderColor: 'divider' }}>
            <ArrowBackIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <Box>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              {esNuevo ? 'Nuevo Albarán' : `Albarán #${albaran?.numero_albaran || ''}`}
            </Typography>
            {!esNuevo && getEstadoChip(albaran?.estado_albaran)}
          </Stack>
            <Typography variant="caption" color="text.secondary">
              {esNuevo
                ? 'Complete la información del comprobante'
                : `Creado el: ${
                    albaran?.fecha_apertura
                      ? (() => {
                          const d = new Date(albaran.fecha_apertura);
                          const meses = [
                            'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
                            'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'
                          ];
                          return `${d.getDate()}-${meses[d.getMonth()]}-${d.getFullYear()}`;
                        })()
                      : 'N/A'
                  }`}
            </Typography>
        </Box>
      </Stack>

      {/* Sección Derecha: Botones de Acción Agrupados */}
      <Stack direction="row" alignItems="center" spacing={1}>
        {/* Acciones Secundarias Directas */}
        {!esNuevo && (
          <>
            <Button
              variant="outlined"
              color="inherit"
              startIcon={<PrintIcon />}
              onClick={onImprimir}
              size="small"
              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
            >
              Imprimir PDF
            </Button>

            {esEditable && (
              <Button
                variant="outlined"
                color="primary"
                startIcon={<CheckCircleIcon />}
                onClick={onCerrar}
                size="small"
                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
              >
                Cerrar Albarán
              </Button>
            )}

            {albaran?.estado_albaran === 'CERRADO' && (
              <Button
                variant="contained"
                color="success"
                startIcon={<ReceiptLongIcon />}
                onClick={onFacturar}
                size="small"
                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, boxShadow: 'none' }}
              >
                Marcar Facturado
              </Button>
            )}
          </>
        )}

        {/* Menú Desplegable de Más Opciones (Para evitar abarrotar la barra) */}
        {!esNuevo && (
          <>
            <Tooltip title="Más opciones">
              <IconButton onClick={handleOpenMenu} size="small" sx={{ border: '1px solid', borderColor: 'divider' }}>
                <MoreVertIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <Menu
              anchorEl={anchorEl}
              open={openMenu}
              onClose={handleCloseMenu}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
              PaperProps={{
                elevation: 3,
                sx: { borderRadius: 2, minWidth: 200, mt: 1 }
              }}
            >
              {onVerReceptor && (
                <MenuItem onClick={() => { handleCloseMenu(); onVerReceptor(); }}>
                  <ListItemIcon><BadgeIcon fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Ver Datos Receptor" />
                </MenuItem>
              )}

              {onVerHistorial && (
                <MenuItem onClick={() => { handleCloseMenu(); onVerHistorial(); }}>
                  <ListItemIcon><HistoryIcon fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Historial de Consumo" />
                </MenuItem>
              )}

              {albaran?.estado_albaran === 'CERRADO' && (
                <MenuItem onClick={() => { handleCloseMenu(); onReabrir(); }}>
                  <ListItemIcon><LockOpenIcon fontSize="small" color="info" /></ListItemIcon>
                  <ListItemText primary="Reabrir Albarán" />
                </MenuItem>
              )}

              <Divider sx={{ my: 0.5 }} />

              {albaran?.estado_albaran !== 'ANULADO' && (
                <MenuItem onClick={() => { handleCloseMenu(); onAnular(); }} sx={{ color: 'error.main' }}>
                  <ListItemIcon><CancelIcon fontSize="small" color="error" /></ListItemIcon>
                  <ListItemText primary="Anular Comprobante" />
                </MenuItem>
              )}
            </Menu>
          </>
        )}
      </Stack>
    </Paper>
  );
};

export default AlbaranHeaderBar;